import React, { useEffect } from 'react';
import { Portal } from '@mantine/core';
import { IconX } from '@tabler/icons-react';
import styles from './FullScreen.module.css';

interface FullScreenProps {
  children: React.ReactNode;
  switch: boolean;
  onClose?: () => void;
  title?: string;
}

export function FullScreen({ children, switch: isOpen, onClose, title }: FullScreenProps) {
  // Handle ESC key to close fullscreen
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen && onClose) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when fullscreen is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return <>{children}</>;
  }

  return (
    <>
      {/* Render children normally when not in fullscreen */}
      {children}

      {/* Fullscreen overlay */}
      <Portal>
        <div className={styles.overlay}>
          <div className={styles.container}>
            {/* Header with close button */}
            <div className={styles.header}>
              {title && <h2 className={styles.title}>{title}</h2>}
              <button
                className={styles.closeButton}
                onClick={onClose}
                aria-label='Close fullscreen'
              >
                <IconX size={20} />
              </button>
            </div>

            {/* Content */}
            <div className={styles.content}>{children}</div>
          </div>
        </div>
      </Portal>
    </>
  );
}
